import { useTranslation } from "react-i18next";
import { UserCheck as UserCheckIcon, UserPlus as UserPlusIcon, UserX as UserXIcon } from "lucide-react";
import type { ContactState } from "@multi-chat/shared";
import { SimpleTooltip } from "../../components/ui/tooltip/index.js";
import { cn } from "../../lib/utils.js";
import { conversationRowContactMark } from "./conversation-list-row.js";

/**
 * Small contact-state icon next to a conversation name. Unsaved and deleted
 * senders always show; the saved mark is opt-in (thread header only).
 */
export function ContactStateMark({
  state,
  showSaved = false,
  size = 14,
}: {
  state: ContactState;
  showSaved?: boolean;
  size?: number;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const mark = conversationRowContactMark(state);
  if (mark == null && !showSaved) return null;

  let label: string;
  let Icon: typeof UserCheckIcon;
  switch (mark) {
    case "unsaved":
      label = t("inbox.unsavedSender");
      Icon = UserPlusIcon;
      break;
    case "deleted":
      label = t("inbox.contactDeleted");
      Icon = UserXIcon;
      break;
    case null:
      label = t("inbox.savedContact");
      Icon = UserCheckIcon;
      break;
    default: {
      const _exhaustive: never = mark;
      return _exhaustive;
    }
  }

  return (
    <SimpleTooltip content={label}>
      <span
        role="img"
        aria-label={label}
        className={cn(
          "inline-grid shrink-0",
          mark == null ? "text-primary" : mark === "deleted" ? "text-inbox-draft" : "text-inbox-subtle",
        )}
      >
        <Icon size={size} aria-hidden="true" />
      </span>
    </SimpleTooltip>
  );
}
